import { createServerFn } from '@tanstack/react-start';
import { z } from 'zod';
import { AdvertiserContractsBoard, AdListBoard, AccountsBoard } from '@api/BoardSDK';
import { withBoardAuth } from '@generated/server/boardAuth.server';
import { requireStaff } from '@generated/server/staff.server';

const COLS = ['paper', 'adSize', 'monthlyPrice', 'section', 'startMonth', 'term', 'active', 'billMonthly', 'howTheyPay', 'billingContact', 'billingEmail', 'billingAddress', 'notes'] as const;
const PAY = z.enum(['Send a Bill', 'Card on File', 'Prepaid']);
const TERMS = ['TFN', '1 Month', '2 Months', '3 Months', '6 Months', '12 Months'] as const;

/** Staff: one page of contracts, optional name search. */
export const listContracts = createServerFn({ method: 'POST' })
  .validator(z.object({ pass: z.string().optional(), q: z.string().max(100).optional(), cursor: z.string().optional() }))
  .handler(async ({ data }) => withBoardAuth(async () => {
    await requireStaff(data.pass);
    const board = new AdvertiserContractsBoard();
    const res = data.cursor
      ? await board.items().withColumns([...COLS]).withPagination({ cursor: data.cursor }).execute()
      : await board.items().withColumns([...COLS]).where(data.q?.trim() ? { name: { contains: data.q.trim() } } : {}).orderBy({ column: 'name', direction: 'asc' }).withPagination({ limit: 50 }).execute();
    // Deleted contracts (and the setup samples) stay on the board but are hidden here.
    const hidden = (n: string) => n.startsWith('[Deleted] ') || n.startsWith('SAMPLE – delete me');
    return { items: res.items.filter((c) => !hidden(c.name)), cursor: res.cursor ?? null };
  }));

const Contract = z.object({
  name: z.string().trim().min(1).max(120),
  paper: z.enum(['Red Hook Star-Revue', 'The Village Star-Revue', 'Both']),
  accountId: z.string().regex(/^\d+$/).optional(),
  adSize: z.string().trim().max(60),
  monthlyPrice: z.number().min(0).max(100000),
  section: z.enum(['ROP', 'Arts', 'Special Section']),
  startMonth: z.string().regex(/^\d{4}-\d{2}$/),
  term: z.enum(TERMS),
  active: z.boolean(),
  billMonthly: z.boolean(),
  howTheyPay: PAY,
  billingContact: z.string().trim().max(120),
  billingEmail: z.string().trim().max(200),
  billingAddress: z.string().trim().max(500),
  notes: z.string().trim().max(2000),
});

/** Staff: create or update a contract. */
export const saveContract = createServerFn({ method: 'POST' })
  .validator(z.object({ pass: z.string().optional(), id: z.string().regex(/^\d+$/).optional(), c: Contract }))
  .handler(async ({ data }) => withBoardAuth(async () => {
    await requireStaff(data.pass);
    const { c } = data;
    const both = c.paper === "Both";
    const price = both ? Math.round((c.monthlyPrice / 2) * 100) / 100 : c.monthlyPrice;
    const base = {
      adSize: c.adSize, monthlyPrice: price, section: c.section,
      startMonth: new Date(`${c.startMonth}-01T00:00:00Z`), term: c.term, active: c.active, howTheyPay: c.howTheyPay, billMonthly: c.howTheyPay === 'Send a Bill',
      billingContact: c.billingContact || null, billingEmail: c.billingEmail ? { email: c.billingEmail, label: c.billingEmail } : null,
      billingAddress: c.billingAddress || null,
      notes: both ? [`Both papers: $${c.monthlyPrice}/month total, split half to each paper.`, c.notes].filter(Boolean).join("\n") : c.notes || null,
      ...(c.accountId ? { account: { linkedItems: [{ id: c.accountId }] } } : {}),
    };
    const board = new AdvertiserContractsBoard();
    if (data.id) {
      const paper = both ? undefined : (c.paper as "Red Hook Star-Revue" | "The Village Star-Revue");
      await board.item(data.id).update({ name: c.name, ...base, ...(paper ? { paper } : {}) }).execute();
      // Keep the ads this contract already placed in step with the edit (finalized tallies are separate copies).
      const ads = new AdListBoard();
      const placed = await ads.items().withColumns(["portalOrderId"]).where({ portalOrderId: { contains: `CONTRACT:${data.id}` } }).withPagination({ limit: 100 }).execute();
      for (const a of placed.items) if (a.portalOrderId === `CONTRACT:${data.id}`) await ads.item(a.id).update({ name: c.name, adSize: c.adSize, cost: price, section: c.section, ...(paper ? { paper } : {}) }).execute();
      return { ok: true };
    }
    const papers = both ? (["Red Hook Star-Revue", "The Village Star-Revue"] as const) : [c.paper as "Red Hook Star-Revue" | "The Village Star-Revue"];
    for (const paper of papers) await board.item().create({ name: c.name, paper, ...base }).execute();
    return { ok: true };
  }));

/** Staff: remove a contract (archived in monday). Already-placed ads stay on their lists. */
export const deleteContract = createServerFn({ method: 'POST' })
  .validator(z.object({ pass: z.string().optional(), id: z.string().regex(/^\d+$/) }))
  .handler(async ({ data }) => withBoardAuth(async () => {
    await requireStaff(data.pass);
    const board = new AdvertiserContractsBoard();
    try { await board.item(data.id).archive().execute(); }
    catch (e) { console.error('[contracts] archive failed, deactivating instead', e); const cur = await board.item(data.id).execute(); await board.item(data.id).update({ name: `[Deleted] ${(cur?.name ?? '').replace(/^\[Deleted\] /, '')}`, active: false, notes: 'Deleted from the portal.' }).execute(); }
    return { ok: true };
  }));

/** Staff: advertiser names for the contract picker — everyone on the Ad List plus the CRM Accounts board. */
export const searchAdvertisers = createServerFn({ method: 'POST' })
  .validator(z.object({ pass: z.string().optional(), q: z.string().max(100).optional() }))
  .handler(async ({ data }) => withBoardAuth(async () => {
    await requireStaff(data.pass);
    const q = data.q?.trim() ?? '';
    const where = q ? { name: { contains: q } } : {};
    // Walk every page (Accounts has several hundred) so the whole A–Z list is available.
    type Page = { items: { id: string; name: string }[]; cursor?: string | null };
    const all = async (first: () => Promise<Page>, next: (c: string) => Promise<Page>) => {
      const out: { id: string; name: string }[] = [];
      try {
        let r = await first();
        for (;;) { out.push(...r.items); if (!r.cursor || out.length >= 1500) break; r = await next(r.cursor); }
      } catch (e) { console.error('[contracts] advertiser list', e); }
      return { items: out };
    };
    const [ads, accounts] = await Promise.all([
      all(() => new AdListBoard().items().where(where).orderBy({ column: 'name', direction: 'asc' }).withPagination({ limit: 100 }).execute(), (c) => new AdListBoard().items().withPagination({ cursor: c }).execute()),
      all(() => new AccountsBoard().items().where(where).orderBy({ column: 'name', direction: 'asc' }).withPagination({ limit: 100 }).execute(), (c) => new AccountsBoard().items().withPagination({ cursor: c }).execute()),
    ]);
    const map = new Map<string, { name: string; accountId?: string }>();
    for (const a of ads.items) { const n = a.name.trim(); if (n && !map.has(n.toLowerCase())) map.set(n.toLowerCase(), { name: n }); }
    for (const a of accounts.items) { const n = a.name.trim(); if (!n) continue; const k = n.toLowerCase(); map.set(k, { name: map.get(k)?.name ?? n, accountId: a.id }); }
    return { names: Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name)).slice(0, 2000) };
  }));

/** Staff: billing details already on file in the CRM for an account (email, contact, address, phone). */
export const getAdvertiserDetails = createServerFn({ method: 'POST' })
  .validator(z.object({ pass: z.string().optional(), accountId: z.string().regex(/^\d+$/) }))
  .handler(async ({ data }) => withBoardAuth(async () => {
    await requireStaff(data.pass);
    const a = await new AccountsBoard().item(data.accountId).withColumns(['email', 'address', 'phone', 'contacts']).execute();
    if (!a) return { email: '', contact: '', address: '', phone: '' };
    const linked = a.contacts?.linkedItems?.map((x) => x.name.trim()) ?? [];
    const linkedEmail = linked.find((n) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(n)) ?? '';
    const contact = linked.find((n) => !n.includes('@')) ?? '';
    return { email: a.email?.email ?? linkedEmail, contact, address: a.address?.address ?? '', phone: a.phone ?? '' };
  }));

/** Staff: this month's ads for Card-on-File contracts, so George can run each card in Stripe. */
export const getCardsToRun = createServerFn({ method: 'POST' })
  .validator(z.object({ pass: z.string().optional(), month: z.string().regex(/^[A-Z][a-z]+ \d{4}$/) }))
  .handler(async ({ data }) => withBoardAuth(async () => {
    await requireStaff(data.pass);
    const [contracts, ads] = await Promise.all([
      new AdvertiserContractsBoard().items().withColumns(['howTheyPay', 'billingEmail', 'billingContact']).where({ howTheyPay: 'Card on File' }).withPagination({ limit: 100 }).execute(),
      new AdListBoard().items().withColumns(['issueMonth', 'paper', 'adSize', 'cost', 'billed', 'portalOrderId']).where({ issueMonth: { contains: data.month }, portalOrderId: { contains: 'CONTRACT:' } }).withPagination({ limit: 100 }).execute(),
    ]);
    const byId = new Map(contracts.items.map((c) => [c.id, c]));
    const rows = ads.items.filter((a) => (a.issueMonth ?? '').trim() === data.month).flatMap((a) => {
      const c = byId.get((a.portalOrderId ?? '').replace('CONTRACT:', ''));
      return c ? [{ id: a.id, name: a.name, paper: a.paper ?? '', adSize: a.adSize ?? '', cost: a.cost ?? 0, paid: a.billed === 'Paid', email: c.billingEmail?.email ?? '', contact: c.billingContact ?? '' }] : [];
    });
    return { rows: rows.sort((x, y) => x.name.localeCompare(y.name)) };
  }));

/** Staff: mark a card-on-file ad as charged (Paid) or undo. */
export const markCharged = createServerFn({ method: 'POST' })
  .validator(z.object({ pass: z.string().optional(), id: z.string().regex(/^\d+$/), paid: z.boolean() }))
  .handler(async ({ data }) => withBoardAuth(async () => {
    await requireStaff(data.pass);
    await new AdListBoard().item(data.id).update({ billed: data.paid ? 'Paid' : 'Not Billed' }).execute();
    return { ok: true };
  }));
